import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import { authV2Selector, authV2InvitationAccept, AUTH_REQUEST_KEYS, toAuthV2Error } from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { completeAuthNavigation } from "@/utils/standaloneConnect";

const AuthInviteAccept = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authV2 = useSelector(authV2Selector);
  const invitationStatus = authV2?.invitationStatus;
  const loading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.invitationAccept]);

  const [searchParams] = useSearchParams();
  const token =
    searchParams.get("token") ?? searchParams.get("invitationToken") ?? searchParams.get("invitation_token") ?? "";

  const handleAccept = async () => {
    const result = await dispatch(authV2InvitationAccept({ token }));

    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);
      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }
    const responseSession = result.payload?.session;
    const hasSwitchedSession =
      typeof responseSession === "object" &&
      responseSession !== null &&
      (responseSession as { switched?: unknown }).switched === true;

    if (hasSwitchedSession) {
      completeAuthNavigation(result.payload);
      return;
    }

    const email = typeof result.payload?.email === "string" ? result.payload.email : "";
    const signInParams = new URLSearchParams();
    if (email) signInParams.set("email", email);
    navigate(`/auth/sign-in?${signInParams.toString()}`);
  };

  return (
    <AuthCard onSubmit={handleAccept}>
      <Stack rowGap={6}>
        <Stack rowGap={3}>
          <Stack flexDirection="column">
            <Typography level="body-lg">Organisation</Typography>
            <Typography level="body-lg" bold>
              {invitationStatus?.invitation?.organisationName}
            </Typography>
          </Stack>
          <Stack flexDirection="column">
            <Typography level="body-lg">Email</Typography>
            <Typography level="body-lg" bold>
              {invitationStatus?.invitation?.email}
            </Typography>
          </Stack>
        </Stack>
        <Button
          size="lg"
          variant="primary"
          endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
          type="submit"
          loading={loading}
        >
          Accept invite
        </Button>
      </Stack>
    </AuthCard>
  );
};

export { AuthInviteAccept };
